import { IconButton, IconButtonProps, CircularProgress } from '@mui/material';

/* See last answer of https://stackoverflow.com/questions/70309561/unable-to-import-svg-with-vite-as-reactcomponent */
import { ReactComponent as ExcelExportIcon } from '../images/ExportExcel.svg';
import { useAppDispatch} from '../appHooks';
import { useExportToExcelQuery } from '../redux/appSettingsAPI';
import * as XLS from 'exceljs';
import FileSaver from 'file-saver';
import React from 'react';
import LogToDB from '../types/dbLogs';
import { ConfirmationDialog } from './ConfirmationDialog';
import { Column } from 'exceljs';



export type ExportExcelProps<T> = {
    svg: React.SVGProps<T>
    label: string
    results: T[]
    total: number;
    exportType?: string;
    columns: Array<Partial<Column>>| undefined;
} & IconButtonProps


const ExportExcel: any = <T,>(props: React.PropsWithChildren<ExportExcelProps<T>>) => {
    const { label, total, results, columns, svg, exportType = "cutters", sx, ...others } = props;
    const dispatch = useAppDispatch();
    const [openDialog, setOpenDialog] = React.useState<boolean>(false);
    const [exportConfirmed, setexportConfirmed] = React.useState<boolean>(false);
    const exportToExcelApi: ReturnType<typeof useExportToExcelQuery> = useExportToExcelQuery({searchString: label, exportType}, {
        skip: !exportConfirmed
    });

    // Must be declared before any early return to satisfy Rules of Hooks
    const ExportRemote = React.useCallback(async (): Promise<void> => {
        try {
            const { data: blob } = exportToExcelApi;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const dateVar: string = new Date().toLocaleString(undefined, {
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', hour12: false, minute: '2-digit', second: '2-digit'
            });
            link.download = `${label.replace(' ', '_')}_${dateVar.replace(' ', '_')}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            await LogToDB('>Excel', `${label.replace(' ', '_')} (${total.toString()})`);
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            dispatch(() => setexportConfirmed(false));
        }
    }, [exportToExcelApi, label, total, dispatch]);

    React.useEffect(() => {
        if (exportToExcelApi.isSuccess) {
            void ExportRemote();
        }
    }, [exportToExcelApi.isSuccess]);

    if (!results || results.length === 0) return (<></>);
    // const searchQuery: ReturnType<typeof useGetCutterByStringQueryState> = useGetCutterByStringQueryState(searchStr);
    const HandleClick: any = async () => {
        if (results.length < total) {
            // Call ConfirmationDialog then exportRemote onClose
            dispatch(() => setOpenDialog(true));
        }
        else {
            await exportLocal();
        }
    }

    const exportLocal: any = async () => {
        const workbook = new XLS.Workbook();
        const worksheet = workbook.addWorksheet(`Results |${label}|`);
        worksheet.columns = columns ?? Object.keys(results[0] as object).map((header: string) => {
            return { key: header, header };
        });
        results.forEach((result: T) => { worksheet.addRow(result); });

        await LogToDB('>Excel', `${label.replace(' ', '_')} (${results.length.toString()})`);

        await workbook.xlsx.writeBuffer().then(data => {
            const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const dateVar: string = new Date().toLocaleString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hour12: false, minute: '2-digit', second: '2-digit' });
            FileSaver.saveAs(blob, `${label.replace(' ', '_')}_${dateVar.replace(' ', '_')}.xlsx`);
        });
    }

    const handleDialogResult: any = async (confirm: boolean) => {
        if (confirm) {
            dispatch(() => setexportConfirmed(true));
        }

        if (openDialog) {
            dispatch(() => setOpenDialog(false));
        }
        console.log(`EXPORT RESUTS REMOTELY ${exportConfirmed ? 'TRUE' : 'FALSE'}`);
    };


    return (
        <>
            <IconButton {...others} onClick={HandleClick} sx={{fill: '#0E796E', ...sx, display: (results.length ?? 0) > 0 ? 'flex-end' : 'none' }} disabled={exportToExcelApi.isFetching}>
                {exportToExcelApi.isFetching ? (
                    <CircularProgress size={24} sx={{ color: '#0E796E' }} />
                ) : (
                    <ExcelExportIcon viewBox='0 0 36 36' {...svg} />
                )}
            </IconButton>
            <ConfirmationDialog Title={`Export ${total} results for '${label}' search?`} open={openDialog}
                Prompt={'Large data exports may impact overall application performance.\r\nPlease use this feature only when necessary.'} 
                setOpened={() => setOpenDialog(openDialog)} 
                OnClose={handleDialogResult} /> 
        </>
    )

}

export default ExportExcel;